import axios from 'axios';
import _omit from 'lodash/omit';
import _range from 'lodash/range';

import { educationDegreeOptions } from 'components/resumeBuilder/steps/education/info';
import { getFilledAndUnFilledSteps, getRBFlow } from 'components/resumeBuilder/steps/flow';
import { STEP_INDICES } from 'components/resumeBuilder/steps/schema';
import { RESUME_BUILDER_INITIAL_STATE } from 'store/initialStates/resumeBuilder';
import { HttpRoutes } from 'utils/constant';

import { RB_DATA, RB_DATA_FIELD, RB_DEFAULT_KEY } from '../components/resumeBuilder/info';
import { generateKey } from '../utils';

const parseLocation = (location, zipcode) => {
  // user used autosuggest
  if (location.valueObj.state) {
    if (location.valueObj.city) {
      return {
        city: location.valueObj.city,
        state: location.valueObj.state,
        ...(zipcode && { zipcode }),
      };
    }
    return {
      state: location.displayValue,
      ...(zipcode && { zipcode }),
    };
  }
  if (location.displayValue) {
    const splitLocation = location.displayValue.split(', ');
    if (splitLocation.length === 1) {
      return {
        city: splitLocation[0],
        ...(zipcode && { zipcode }),
      };
    }
    return {
      city: splitLocation[0],
      state: splitLocation[1].trim(),
      ...(zipcode && { zipcode }),
    };
  }
};

const setStepDataUpdate = (currentState = {}, payload = {}) => ({
  ...currentState,
  [payload.dataName]: {
    ...(currentState[payload.dataName] || {}),
    [payload.dataKey]: {
      ...((currentState[payload.dataName] || {})[payload.dataKey] || {}),
      [payload.field]: payload.value,
    },
  },
});

const jobLevelStepUpdate = (data = {}) => ({
  [RB_DATA.COMMON]: {
    [RB_DEFAULT_KEY.COMMON]: {
      [RB_DATA_FIELD.COMMON.JOB_LEVEL]: (data?.professionalSummary?.zippiaJobLevels || []).map(
        (jobLevel) => ({ value: jobLevel })
      ),
    },
  },
});

const headerStepUpdate = (data = {}) => {
  let currentState = {};
  const dataKey = RB_DEFAULT_KEY.HEADER;
  const dataName = RB_DATA.HEADER;
  const { HEADER } = RB_DATA_FIELD;
  const updateField = (field, value) =>
    setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field,
      value,
    });
  if (data.firstName) currentState = updateField(HEADER.FIRST_NAME, data.firstName);
  if (data.lastName) currentState = updateField(HEADER.LAST_NAME, data.lastName);
  if (data.email) currentState = updateField(HEADER.EMAIL, data.email);
  if (data.phone) currentState = updateField(HEADER.PHONE, data.phone);
  if (data.socialWebsites && data.socialWebsites.length !== 0)
    currentState = updateField(HEADER.SOCIALS, { show: true, items: data.socialWebsites });
  if (data.otherWebsites)
    currentState = updateField(HEADER.PORTFOLIOS, {
      show: !!data.otherWebsites.length,
      items: data.otherWebsites.length ? data.otherWebsites : [{ websiteName: '', url: '' }],
    });
  if (data.location && (data.location.state || data.location.city)) {
    const location = {};
    if (data.location.state && data.location.city) {
      location.displayValue = `${data.location.city}, ${data.location.state}`;
      location.valueObj = { city: data.location.city, state: data.location.state };
      currentState = updateField(HEADER.LOCATION, location);
    }
    if (data.location.city && !data.location.state) {
      location.displayValue = `${data.location.city}`;
      location.valueObj = { city: data.location.city, state: '' };
      currentState = updateField(HEADER.LOCATION, location);
    }
    if (!data.location.city && data.location.state) {
      location.displayValue = `${data.location.state}`;
      location.valueObj = { city: '', state: data.location.state };
      currentState = updateField(dataKey, HEADER.LOCATION, location);
    }
  }
  if (data.location?.zipcode) currentState = updateField(HEADER.ZIP_CODE, data.location.zipcode);
  return currentState;
};

const workStepUpdate = (data = {}) => {
  let currentState = {};
  const dataName = RB_DATA.WORK_HISTORY;
  const { WORK_HISTORY } = RB_DATA_FIELD;
  const updateField = (dataKey, field, value) =>
    setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field,
      value,
    });
  if (data.experienceDetails && data.experienceDetails.length !== 0) {
    data.experienceDetails.forEach((job, jobIndex) => {
      const dataKey = generateKey(`${job.company}_${job.title}_${dataName}`);
      if (job.summary) currentState = updateField(dataKey, WORK_HISTORY.DESCRIPTION, job.summary);
      if (job?.title && job?.zippiaTitle)
        currentState = updateField(dataKey, WORK_HISTORY.JOB_TITLE, {
          title: job.title,
          zippiaTitle: job.zippiaTitle,
        });
      else if (job.title)
        currentState = updateField(dataKey, WORK_HISTORY.JOB_TITLE, { title: job.title });
      if (job.company) {
        const company = {
          displayValue: job.company,
        };

        if (job.zippiaCompanyID) {
          company.value = job.zippiaCompanyID;
        }

        currentState = updateField(dataKey, WORK_HISTORY.EMPLOYER, company);
      }
      if (job.isCurrentPosition)
        currentState = updateField(dataKey, WORK_HISTORY.IS_CURRENT, job.isCurrentPosition);
      if (job.durationFrom)
        currentState = updateField(dataKey, WORK_HISTORY.START_DATE, job.durationFrom);
      if (job.durationTo)
        currentState = updateField(dataKey, WORK_HISTORY.END_DATE, job.durationTo);
      if (job.location && (job.location.state || job.location.city)) {
        const location = {};
        if (job.location.state && job.location.city) {
          location.displayValue = `${job.location.city}, ${job.location.state}`;
          location.valueObj = { city: job.location.city, state: job.location.state };
          currentState = updateField(dataKey, WORK_HISTORY.LOCATION, location);
        }
        if (job.location.city && !job.location.state) {
          location.displayValue = `${job.location.city}`;
          location.valueObj = { city: job.location.city, state: '' };
          currentState = updateField(dataKey, WORK_HISTORY.LOCATION, location);
        }
        if (!job.location.city && job.location.state) {
          location.displayValue = `${job.location.state}`;
          location.valueObj = { city: '', state: job.location.state };
          currentState = updateField(dataKey, WORK_HISTORY.LOCATION, location);
        }
      }

      currentState = updateField(dataKey, WORK_HISTORY.ORDER, jobIndex + 1);
    });
  }
  return currentState;
};

const educationStepUpdate = (data = {}) => {
  let currentState = {};
  const dataName = RB_DATA.EDUCATION;
  const { EDUCATION } = RB_DATA_FIELD;
  const updateField = (dataKey, field, value) =>
    setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field,
      value,
    });
  if (data.educationalDetails && data.educationalDetails.length !== 0) {
    data.educationalDetails.forEach((school, educationIndex) => {
      const dataKey = generateKey(dataName);
      if (school.durationFrom)
        currentState = updateField(dataKey, EDUCATION.GRADUATION_START_DATE, school.durationFrom);
      if (school.durationTo)
        currentState = updateField(dataKey, EDUCATION.GRADUATION_END_DATE, school.durationTo);
      if (school.major) currentState = updateField(dataKey, EDUCATION.FIELD_OF_STUDY, school.major);
      if (school.zippiaDegree)
        currentState = updateField(dataKey, EDUCATION.DEGREE, school.zippiaDegree);
      if (school.school) currentState = updateField(dataKey, EDUCATION.SCHOOL_NAME, school.school);
      if (school.isCurrentSchool)
        currentState = updateField(dataKey, EDUCATION.IS_CURRENT, school.isCurrentSchool);
      if (school.location && (school.location.state || school.location.city)) {
        const location = {};
        if (school.location.state && school.location.city) {
          location.displayValue = `${school.location.city}, ${school.location.state}`;
          location.valueObj = { city: school.location.city, state: school.location.state };
          currentState = updateField(dataKey, EDUCATION.SCHOOL_LOCATION, location);
        }
        if (school.location.city && !school.location.state) {
          location.displayValue = `${school.location.city}`;
          location.valueObj = { city: school.location.city, state: '' };
          currentState = updateField(dataKey, EDUCATION.SCHOOL_LOCATION, location);
        }
        if (!school.location.city && school.location.state) {
          location.displayValue = `${school.location.state}`;
          location.valueObj = { city: '', state: school.location.state };
          currentState = updateField(dataKey, EDUCATION.SCHOOL_LOCATION, location);
        }
      }
      if (school.summary && school.summary !== '<p><br></p>')
        currentState = updateField(dataKey, EDUCATION.SUMMARY, school.summary);

      currentState = updateField(dataKey, EDUCATION.ORDER, educationIndex + 1);
    });
  }
  if (data?.professionalSummary?.zippiaDegree) {
    currentState[RB_DATA.COMMON] = {
      [RB_DEFAULT_KEY.COMMON]: {
        [RB_DATA_FIELD.COMMON.EDUCATION_LEVEL]: data?.professionalSummary?.zippiaDegree
          ? [{ value: data?.professionalSummary?.zippiaDegree }]
          : [],
      },
    };
  }
  return currentState;
};

const skillsStepUpdate = (data = {}) => {
  let currentState = {};
  const dataName = RB_DATA.SKILLS;
  const { SKILLS } = RB_DATA_FIELD;
  const updateField = (dataKey, field, value) =>
    setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field,
      value,
    });

  if (
    data.professionalSummary &&
    data.professionalSummary.skillSet &&
    data.professionalSummary.skillSet.length !== 0
  ) {
    data.professionalSummary.skillSet.forEach((skill) => {
      const dataKey = generateKey(skill?.skillName || skill);
      currentState = updateField(dataKey, SKILLS.SKILL_NAME, skill);
    });
  }
  return currentState;
};

const extraStepUpdate = (data = {}) => {
  let currentState = {};
  const updateField = (dataName, dataKey, field, value) =>
    setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field,
      value,
    });

  if (data.languages && data.languages.length !== 0) {
    const dataName = RB_DATA.EXTRA_LANGUAGES;
    const { EXTRA_LANGUAGES } = RB_DATA_FIELD;
    data.languages.forEach((language) => {
      const dataKey = generateKey(language?.languageName || language);
      currentState = updateField(dataName, dataKey, EXTRA_LANGUAGES.LANGUAGE_NAME, {
        languageName: language?.languageName,
        normalizedLanguageName: language?.normalizedLanguageName,
      });
    });
    currentState = updateField(
      RB_DATA.EXTRA,
      RB_DEFAULT_KEY.EXTRA_DEFAULT,
      RB_DATA_FIELD.EXTRA.INCLUDE_LANGUAGES,
      true
    );
  }
  if (data.certifications && data.certifications.length !== 0) {
    const dataName = RB_DATA.EXTRA_CERTIFICATIONS;
    const { EXTRA_CERTIFICATIONS } = RB_DATA_FIELD;
    data.certifications.forEach((certificate) => {
      const dataKey = generateKey(certificate.certificationName);
      if (certificate.issueDate)
        currentState = updateField(
          dataName,
          dataKey,
          EXTRA_CERTIFICATIONS.CERTIFICATION_DATE,
          certificate.issueDate
        );
      if (certificate.normalizedCertificationName || certificate.certificationName) {
        currentState = updateField(dataName, dataKey, EXTRA_CERTIFICATIONS.CERTIFICATION_NAME, {
          certificationName: certificate?.certificationName,
          normalizedCertificationName: certificate?.normalizedCertificationName,
        });
      }
    });
    currentState = updateField(
      RB_DATA.EXTRA,
      RB_DEFAULT_KEY.EXTRA_DEFAULT,
      RB_DATA_FIELD.EXTRA.INCLUDE_CERTIFICATIONS,
      true
    );
  }
  return currentState;
};

const summaryStepUpdate = (data = {}) => {
  let currentState = {};
  const dataName = RB_DATA.SUMMARY;
  const dataKey = RB_DEFAULT_KEY.SUMMARY;
  if (data.summary) {
    currentState = setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field: RB_DATA_FIELD.SUMMARY.DESCRIPTION,
      value: data.summary,
    });
  }
  if (data.headline) {
    currentState = setStepDataUpdate(currentState, {
      dataName,
      dataKey,
      field: RB_DATA_FIELD.SUMMARY.HEADLINE,
      value: data.headline,
    });
  }
  return currentState;
};

const getCustomSubSteps = (subSteps = {}, customSections = []) => {
  const newSubSteps = {};
  const subStepsLength = Object.keys(subSteps).length;
  customSections.forEach((customSection, i) => {
    newSubSteps[i + subStepsLength] = {
      index: i + subStepsLength,
      title: customSection?.sectionName || '',
      url: `custom-${customSection?.sectionId}`,
      pageTitle: `Add ${customSection?.sectionName || ''} - Zippia`,
    };
  });
  return newSubSteps;
};

const ResumeBuilderService = {
  fitDataToShema(resumeData) {
    const { data, currentStepIndex, currentSubStepIndex, professionalSummary } = resumeData;

    const schemaData = {};

    if (data.isCompleted) schemaData.isCompleted = data.isCompleted;
    if (data.resumeTemplate) schemaData.resumeTemplate = data.resumeTemplate;

    switch (currentStepIndex) {
      case STEP_INDICES.CONTACT: {
        const headerFields = Object.values(data.HEADER || {});
        const { HEADER } = RB_DATA_FIELD;
        const contactDetails = data[RB_DATA.HEADER]?.[RB_DEFAULT_KEY.HEADER] || {};
        const firstName = contactDetails?.[RB_DATA_FIELD.HEADER.FIRST_NAME];
        const lastName = contactDetails?.[RB_DATA_FIELD.HEADER.LAST_NAME];
        const email = contactDetails?.[RB_DATA_FIELD.HEADER.EMAIL];
        if (firstName) schemaData.firstName = firstName;
        if (lastName) schemaData.lastName = lastName;
        if (email) schemaData.email = email;
        headerFields.forEach((field) => {
          if (field[HEADER.PHONE]) {
            schemaData.phone = field[HEADER.PHONE] || '';
          }
          if (field[HEADER.SOCIALS] && field[HEADER.SOCIALS] !== 0 && field[HEADER.SOCIALS].items) {
            schemaData.socialWebsites =
              field[HEADER.SOCIALS].items?.filter((item) => item.url) || [];
          }
          if (field[HEADER.PORTFOLIOS] && field[HEADER.PORTFOLIOS].items) {
            schemaData.otherWebsites =
              field[HEADER.PORTFOLIOS].items?.filter((item) => item.url) || [];
          }
          if (field[HEADER.LOCATION]) {
            schemaData.location = parseLocation(field[HEADER.LOCATION], field[HEADER.ZIP_CODE]);
            if (field[HEADER.LOCATION].valueObj.state) {
              schemaData.normalizedLocation = parseLocation(field[HEADER.LOCATION]);
            }
          }
        });
        break;
      }
      case STEP_INDICES.EDUCATION: {
        const addZippiaDegree = () => {
          schemaData.professionalSummary = { ...professionalSummary };
          const degreeAnswer = data?.COMMON?.COMMON_DEFAULT?.COMMON_EDUCATION_LEVEL?.[0]?.answer;
          if (degreeAnswer) schemaData.professionalSummary.zippiaDegree = degreeAnswer;
          else {
            delete schemaData.professionalSummary.zippiaDegree;
          }
        };

        const addEducationDetails = () => {
          const educations = Object.values(data.EDUCATION || {});
          const { EDUCATION } = RB_DATA_FIELD;
          schemaData.educationalDetails = educations.map((education) => ({
            ...((education[EDUCATION.SCHOOL_NAME] && {
              school: education[EDUCATION.SCHOOL_NAME],
            }) ||
              ''),
            ...((education[EDUCATION.SCHOOL_NAME_NORMALIZED] && {
              zippiaSchool: education[EDUCATION.SCHOOL_NAME_NORMALIZED],
            }) ||
              ''),
            ...((education[EDUCATION.FIELD_OF_STUDY] && {
              major: education[EDUCATION.FIELD_OF_STUDY],
            }) ||
              ''),
            ...((education[EDUCATION.FIELD_OF_STUDY_NORMALIZED] && {
              zippiaMajor: education[EDUCATION.FIELD_OF_STUDY_NORMALIZED],
            }) ||
              ''),
            ...((education[EDUCATION.DEGREE] && { zippiaDegree: education[EDUCATION.DEGREE] }) ||
              ''),
            ...((education[EDUCATION.GRADUATION_START_DATE] && {
              durationFrom: education[EDUCATION.GRADUATION_START_DATE],
            }) ||
              ''),
            ...((education[EDUCATION.GRADUATION_END_DATE] && {
              durationTo: education[EDUCATION.GRADUATION_END_DATE],
            }) ||
              ''),
            isCurrentSchool: education[EDUCATION.IS_CURRENT] || false,
            ...((education[EDUCATION.SCHOOL_LOCATION]?.displayValue && {
              location: parseLocation(education[EDUCATION.SCHOOL_LOCATION]),
            }) ||
              ''),
            ...((education[EDUCATION.SCHOOL_LOCATION]?.valueObj.state && {
              normalizedLocation: parseLocation(education[EDUCATION.SCHOOL_LOCATION]),
            }) ||
              ''),
            ...((education[EDUCATION.SUMMARY] &&
              education[EDUCATION.SUMMARY] !== '<p><br></p>' && {
                summary: education[EDUCATION.SUMMARY],
              }) ||
              ''),
          }));
        };
        switch (currentSubStepIndex) {
          case 0:
            addZippiaDegree();
            break;
          case 1:
            addEducationDetails();
            break;
          default:
            addZippiaDegree();
            addEducationDetails();
        }
        break;
      }
      case STEP_INDICES.WORK_HISTORY: {
        const workExperiences = Object.values(data.WORK_HISTORY || {});
        const { WORK_HISTORY } = RB_DATA_FIELD;
        schemaData.experienceDetails = workExperiences.map((workExperience) => ({
          ...((workExperience[WORK_HISTORY.EMPLOYER]?.displayValue && {
            company: workExperience[WORK_HISTORY.EMPLOYER].displayValue,
          }) ||
            ''),
          ...((workExperience[WORK_HISTORY.EMPLOYER]?.value && {
            zippiaCompanyID: workExperience[WORK_HISTORY.EMPLOYER].value,
          }) ||
            ''),
          ..._omit(workExperience[WORK_HISTORY.JOB_TITLE], 'article'),
          ...((workExperience[WORK_HISTORY.DESCRIPTION] && {
            summary: workExperience[WORK_HISTORY.DESCRIPTION],
          }) ||
            ''),
          ...((workExperience[WORK_HISTORY.START_DATE] && {
            durationFrom: workExperience[WORK_HISTORY.START_DATE],
          }) ||
            ''),
          ...((workExperience[WORK_HISTORY.END_DATE] && {
            durationTo: workExperience[WORK_HISTORY.END_DATE],
          }) ||
            ''),
          isCurrentPosition: workExperience[WORK_HISTORY.IS_CURRENT] || false,
          ...((workExperience[WORK_HISTORY.LOCATION]?.displayValue && {
            location: parseLocation(workExperience[WORK_HISTORY.LOCATION]),
          }) ||
            ''),
          ...((workExperience[WORK_HISTORY.LOCATION]?.valueObj.state && {
            normalizedLocation: parseLocation(workExperience[WORK_HISTORY.LOCATION]),
          }) ||
            ''),
        }));
        break;
      }
      case STEP_INDICES.SUMMARY: {
        const summary = data?.SUMMARY?.SUMMARY_DEFAULT?.SUMMARY_DESCRIPTION;
        const headline = data?.SUMMARY?.SUMMARY_DEFAULT?.SUMMARY_HEADLINE;
        if (summary !== '<p><br></p>') {
          schemaData.summary = summary;
        }
        // No need to validate the headline since the user could endup with no custom headline and use the last experience
        if (headline) schemaData.headline = headline;
        break;
      }
      case STEP_INDICES.SKILLS: {
        schemaData.professionalSummary = { ...professionalSummary };
        const skills = Object.values(data.SKILLS || {});
        if (skills[0]?.SKILLS_SKILL_NAME) {
          const skillSet = [];
          skills.forEach((skill) => {
            if (skill[RB_DATA_FIELD.SKILLS.SKILL_NAME]?.skillName) {
              skillSet.push(skill[RB_DATA_FIELD.SKILLS.SKILL_NAME]);
            }
          });
          schemaData.professionalSummary.skillSet = skillSet;
        } else {
          delete schemaData.professionalSummary.skillSet;
        }
        break;
      }
      case STEP_INDICES.INTERESTS: {
        const addCertifictions = () => {
          if (
            data[RB_DATA.EXTRA]?.[RB_DEFAULT_KEY.EXTRA_DEFAULT]?.[
              RB_DATA_FIELD.EXTRA.INCLUDE_CERTIFICATIONS
            ]
          ) {
            const certifications = Object.values(data.EXTRA_CERTIFICATIONS || {});
            schemaData.certifications = certifications
              .map((certificate) => ({
                ...((certificate[RB_DATA_FIELD.EXTRA_CERTIFICATIONS.CERTIFICATION_DATE] && {
                  issueDate: certificate[RB_DATA_FIELD.EXTRA_CERTIFICATIONS.CERTIFICATION_DATE],
                }) ||
                  ''),
                ...certificate[RB_DATA_FIELD.EXTRA_CERTIFICATIONS.CERTIFICATION_NAME],
              }))
              .filter((item) => item.certificationName);
          } else {
            schemaData.certifications = [];
          }
        };

        const addLanguages = () => {
          if (
            data[RB_DATA.EXTRA]?.[RB_DEFAULT_KEY.EXTRA_DEFAULT]?.[
              RB_DATA_FIELD.EXTRA.INCLUDE_LANGUAGES
            ]
          ) {
            const languages = Object.values(data.EXTRA_LANGUAGES || {});
            schemaData.languages = languages
              .map((language) => language[RB_DATA_FIELD.EXTRA_LANGUAGES.LANGUAGE_NAME])
              .filter((item) => item.languageName);
          } else {
            schemaData.languages = [];
          }
        };

        const addCustomSections = () => {
          schemaData.customSections =
            Object.values(data.customSections || {}).map((item) => _omit(item, ['error'])) || [];
        };

        switch (currentSubStepIndex) {
          case -1:
          case 0:
            addCertifictions();
            addLanguages();
            addCustomSections();
            break;
          case 1: {
            addCertifictions();
            break;
          }
          case 2: {
            addLanguages();
            break;
          }
          default:
            addCustomSections();
        }
        break;
      }
    }
    return schemaData;
  },

  saveResumeBuilderData(resumeData) {
    return axios
      .put(HttpRoutes.resumeBuilderData, resumeData)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },

  getResumeBuilderData: () =>
    axios
      .get(HttpRoutes.resumeBuilderData)
      .then((response) => response.data.resumeBuilderData)
      .catch((error) => {
        console.error({
          msg: 'Error occurred in ResumeBuilderService.getResumeBuilderData',
          error,
        });
        return '';
      }),

  getInitialResumeBuilderState: function (
    resumeData = {},
    stripeSubscriptionData = {},
    queryParams = {},
    myResumePage = false,
    user
  ) {
    const { step: currentStepUrl, skills } = queryParams;
    const skillSet = skills
      ? skills.split(',').map((skillName) => ({
          skillName,
          normalizedSkill: skillName,
        }))
      : [];

    const resumeBuilderState = {
      ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState,
      [STEP_INDICES.EXPERIENCE_LEVEL]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.EXPERIENCE_LEVEL],
        data: jobLevelStepUpdate(resumeData),
      },
      [STEP_INDICES.CONTACT]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.CONTACT],
        data: headerStepUpdate(resumeData),
      },
      [STEP_INDICES.SUMMARY]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.SUMMARY],
        data: summaryStepUpdate(resumeData),
      },
      [STEP_INDICES.WORK_HISTORY]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.WORK_HISTORY],
        data: workStepUpdate(resumeData),
      },
      [STEP_INDICES.EDUCATION]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.EDUCATION],
        data: educationStepUpdate(resumeData),
      },
      [STEP_INDICES.SKILLS]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.SKILLS],
        data: skillsStepUpdate({
          professionalSummary: {
            ...(resumeData?.professionalSummary || {}),
            skillSet: skillSet?.length ? skillSet : resumeData?.professionalSummary?.skillSet,
          },
        }),
      },
      [STEP_INDICES.INTERESTS]: {
        ...RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.INTERESTS],
        subSteps: {
          ...(RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.INTERESTS]?.subSteps ||
            {}),
          ...getCustomSubSteps(
            RESUME_BUILDER_INITIAL_STATE.resumeBuilderState[STEP_INDICES.INTERESTS]?.subSteps,
            resumeData?.customSections
          ),
        },
        data: {
          ...extraStepUpdate(resumeData),
          ...(resumeData?.customSections?.length
            ? {
                customSections: resumeData?.customSections.reduce(
                  (customSections, customSection) => ({
                    ...customSections,
                    [customSection.sectionId]: customSection,
                  }),
                  {}
                ),
              }
            : {}),
        },
      },
    };

    const { navigateStepByStepUrl } = getRBFlow({
      user,
      currentRBState: resumeBuilderState,
    });

    let flow = RESUME_BUILDER_INITIAL_STATE.flow;
    let previewStepCompletedSectionsModal =
      RESUME_BUILDER_INITIAL_STATE.previewStepCompletedSectionsModal;

    if (myResumePage) {
      flow = {
        ...flow,
        currentStepIndex: 10,
        visitedSteps: _range(0, 11),
        currentSubStepIndex: 0,
        nextBtnLabel: 'Next',
      };
    } else if (currentStepUrl) {
      const nextFlow = navigateStepByStepUrl(currentStepUrl);
      flow = {
        ...flow,
        currentStepIndex: nextFlow.nextStepIndex,
        currentSubStepIndex: nextFlow.nextSubStepIndex,
        nextBtnLabel: nextFlow.nextBtnLabel,
        visitedSteps: _range(0, nextFlow.nextStepIndex),
      };
    }

    return {
      ...RESUME_BUILDER_INITIAL_STATE,
      resumeBuilderState,
      theme: resumeData.resumeTemplate,
      myResumePage,
      flow,
      professionalSummary: resumeData.professionalSummary || {},
      id: resumeData._id,
      previewStepCompletedSectionsModal: {
        ...previewStepCompletedSectionsModal,
        ...getFilledAndUnFilledSteps(resumeBuilderState),
      },
      stripeSubscriptionData,
      isCompleted: !!resumeData.isCompleted,
    };
  },

  getCertSuggestions: (data) =>
    axios
      .post(HttpRoutes.certSuggestions, data)
      .then((response) => Promise.resolve(response.data))
      .catch((error) => {
        console.error({ msg: 'Error occurred in getting cert suggestions', error });
        return Promise.reject(error);
      }),

  getSkillsSuggestions: (data) =>
    axios
      .post(HttpRoutes.skillsSuggestions, data)
      .then((response) => Promise.resolve(response.data))
      .catch((error) => {
        console.error({ msg: 'Error occurred in getting skills suggestions', error });
        return Promise.reject(error);
      }),

  getCertificationsSuggestions: (data) =>
    axios
      .post(HttpRoutes.certificationsSuggestions, data)
      .then((response) => Promise.resolve(response.data))
      .catch((err) => {
        console.error({ msg: 'Error occured in getting certifications suggestions', err });
        return Promise.reject(err);
      }),

  getRandomDescription: (data) =>
    axios
      .post(HttpRoutes.randomDescription, data)
      .then((response) => Promise.resolve(response.data))
      .catch((error) => {
        console.error({ msg: 'Error occurred in getting random descriptions', error });
        return Promise.reject(error);
      }),

  parseEducationDegreeText: (text) => {
    const valueObj = educationDegreeOptions.find((item) => item.text === text) || {
      text: '',
      value: '',
    };
    return valueObj.value || '';
  },

  parseEducationDegreeValue: (value) => {
    const valueObj = educationDegreeOptions.find((item) => item.value === value) || {
      text: '',
      value: '',
    };
    return valueObj.text || '';
  },
};

export default ResumeBuilderService;
